/**
 * Permission definition for the RBAC system
 */
export interface Permission {
  /** Unique identifier for the permission */
  id: string;
  /** Human-readable name of the permission */
  name: string;
  /** Detailed description of what the permission allows */
  description: string;
  /** Logical grouping of the permission */
  category: PermissionCategory;
}

/**
 * Categories for grouping permissions
 */
export enum PermissionCategory {
  GENERAL = 'General',
  SYSTEM = 'System',
  STAFF = 'Staff',
  CUSTOMERS = 'Customers',
  EQUIPMENT = 'Equipment',
  OPERATIONS = 'Operations',
  BUSINESS = 'Business',
  MARINA = 'Marina',
  STORE = 'Store',
  KITCHEN = 'Kitchen',
  FINANCE = 'Finance'
}

/**
 * Role definition for the RBAC system
 */
export interface Role {
  /** Unique identifier for the role */
  id: string;
  /** Human-readable name of the role */
  name: string;
  /** Detailed description of the role's purpose */
  description: string;
  /** List of permission IDs granted to this role */
  grantedPermissions: string[];
  /** Timestamp when the role was created */
  createdAt: Date;
  /** Timestamp when the role was last updated */
  updatedAt: Date;
}

/**
 * Permission check result
 */
export interface PermissionCheckResult {
  /** Whether the permission is granted */
  granted: boolean;
  /** The reason why the permission was denied (if applicable) */
  reason?: string;
}

/**
 * Available permission IDs in the system
 */
export enum PermissionId {
  // General
  VIEW_DASHBOARD = 'view_dashboard',
  
  // System
  MANAGE_USERS = 'manage_users',
  MANAGE_STAFF = 'manage_staff',
  MANAGE_ROLES = 'manage_roles',
  MANAGE_SETTINGS = 'manage_settings',
  
  // Staff
  VIEW_STAFF = 'view_staff',
  EDIT_STAFF = 'edit_staff',
  DELETE_STAFF = 'delete_staff',
  
  // Customers
  MANAGE_CUSTOMERS = 'manage_customers',
  VIEW_CUSTOMERS = 'view_customers',
  EDIT_CUSTOMERS = 'edit_customers',
  
  // Equipment
  MANAGE_VESSELS = 'manage_vessels',
  VIEW_VESSELS = 'view_vessels',
  MANAGE_TRAILERS = 'manage_trailers',
  VIEW_TRAILERS = 'view_trailers',
  
  // Operations
  MANAGE_MAINTENANCE = 'manage_maintenance',
  VIEW_MAINTENANCE = 'view_maintenance',
  MANAGE_RESERVATIONS = 'manage_reservations',
  VIEW_RESERVATIONS = 'view_reservations',
  
  // Marina
  MANAGE_MARINA = 'manage_marina',
  VIEW_MARINA = 'view_marina',
  MANAGE_LAUNCH = 'manage_launch',
  VIEW_LAUNCH = 'view_launch',
  
  // Store
  MANAGE_STORE = 'manage_store',
  VIEW_STORE = 'view_store',
  MANAGE_INVENTORY = 'manage_inventory',
  VIEW_INVENTORY = 'view_inventory',
  
  // Kitchen
  MANAGE_KITCHEN = 'manage_kitchen',
  VIEW_KITCHEN = 'view_kitchen',
  MANAGE_MENU = 'manage_menu',
  VIEW_MENU = 'view_menu',
  
  // Business
  MANAGE_PRICING = 'manage_pricing',
  VIEW_PRICING = 'view_pricing',
  MANAGE_REPORTS = 'manage_reports',
  VIEW_REPORTS = 'view_reports',
  
  // Finance
  MANAGE_FINANCE = 'manage_finance',
  VIEW_FINANCE = 'view_finance',
  MANAGE_INVOICES = 'manage_invoices',
  VIEW_INVOICES = 'view_invoices'
} 